import {Typed} from "app/stimulus/typed-stimulus";
import {Controller} from "@hotwired/stimulus";
import {AsStimulusController} from "app/stimulus/as-stimulus-controller";
import {Component} from "@symfony/ux-live-component";
import {onLiveComponentRender} from "app/stimulus/live-component-render";
import {changeValue} from "app/utils/dom";

/**
 * Switches a limit row between the view and the edit state. The state is written to the hidden editing
 * field, so a surrounding live component renders the same state again. Live re-applies the classes
 * changed here on top of every render, so after each render the controller sets them by the model again,
 * e.g. for a row closed by discarding the changes.
 */
@AsStimulusController('card-limits-form-row')
export default class extends Typed(
    Controller<HTMLElement>, {
        targets: {
            value: HTMLElement,
            editing: HTMLInputElement,
            editButton: HTMLElement,
            viewOnly: HTMLElement,
            editOnly: HTMLElement,
        },
    }
)
{
    private removeRenderListener: (() => void) | null = null;

    public connect(): void {
        this.removeRenderListener = onLiveComponentRender(this.element, (component) => {
            this.syncWithRender(component);
        });
    }

    public disconnect(): void {
        this.removeRenderListener?.();
        this.removeRenderListener = null;
    }

    public edit(): void {
        this.setEditing(true);
        this.valueTarget.focus();
    }

    public done(event: Event): void {
        event.preventDefault();
        this.setEditing(false);
        this.editButtonTarget.focus();
    }

    /**
     * Enter in the value closes the row instead of submitting the whole form; Stimulus 3.1 has no key filters.
     */
    public doneOnEnter(event: KeyboardEvent): void {
        if (event.key === 'Enter') {
            this.done(event);
        }
    }

    public clear(): void {
        const input = this.valueTarget;
        if (input instanceof HTMLInputElement || input instanceof HTMLSelectElement) {
            changeValue(input, '');
        }
        input.focus();
    }

    /**
     * The model keeps the state including the changes made while a request was running, unlike the
     * rendered HTML; a row with an error stays open, so the error is visible.
     */
    private syncWithRender(component: Component): void {
        const hasError = this.element.querySelector('.form__errors') !== null;
        this.setEditing(component.getData(this.editingTarget.name) === '1' || hasError);
    }

    private setEditing(editing: boolean): void {
        this.viewOnlyTargets.forEach((element) => element.classList.toggle('hidden', editing));
        this.editOnlyTargets.forEach((element) => element.classList.toggle('hidden', !editing));
        this.editButtonTarget.setAttribute('aria-expanded', editing ? 'true' : 'false');
        changeValue(this.editingTarget, editing ? '1' : '');
    }
}
